<script setup lang="ts">
import { CirclePlus } from "@element-plus/icons-vue";
import { useRefHistory } from "@vueuse/core";
import { ElMessage, ElMessageBox } from "element-plus";
import { computed, onMounted, ref, watch } from "vue";
import { Entity, LoginAccountInSysOrgDto, SysOrg, SysOrgRoleName } from "~/api";
import { useEntityDefinitionStore } from "~/stores/EntityDefinitionStore";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { localLoginAccount } from "~/types/MiscTypes";
import { apiClient } from "~/utils/apiClient";
import { drawerWidth } from "~/utils/constants";
import { isValidEmailAddress } from "~/utils/emailUtils";
import { showError } from "~/utils/handleAPIError";
import { arraysAreIdentical } from "~/utils/miscUtils";
import EntityResponseViewer from "../components/EntityResponseViewer.vue";
import EntityInstanceCreator from "./EntityInstanceCreator.vue";

const showDebugInfo = false;

const props = defineProps<{
  user: localLoginAccount;
  existingEmails: { userGuid: string; email: string | undefined }[]; // including our own email
  drawerOpen: boolean;
  addingNewUser: boolean;
  whoToShow: SysOrgRoleName;
  userList?: localLoginAccount[];
}>();

const emit = defineEmits<{
  (e: "updated", value: LoginAccountInSysOrgDto): void;
  (e: "drawerClosed"): void;
}>();

const myInfoStore = useMyInfoStore();
const entityDefinitionStore = useEntityDefinitionStore();

const keySendEmail = ref(0);

const entities = ref<Entity[]>([]);

const user = ref<localLoginAccount>(props.user);
const addingNewUser = ref(props.addingNewUser);
const isTempAccount = computed(() => user.value.isTempAccount);
const showSendEmail = ref(!addingNewUser.value && isTempAccount.value);

const hasUserAdminRole = myInfoStore.currentSysOrg.roles.includes(
  SysOrgRoleName.USER_ADMIN,
);

// const userAdminCheckbox = ref();
/** The list of all orgs from the server */
const allOrgs = ref<SysOrg[]>([]);

const userOrgs = computed(() => {
  return allOrgs.value.filter(
    (org) => org.guid && user.value.sysOrgGuids?.includes(org.guid),
  );
});

const entityDrawerOpen = ref(false);
const entityInstanceCreatorOpen = ref(false);

type RoleInfo = {
  code: SysOrgRoleName;
  display: string;
  comment: string;
  level: number;
};
const setupRoleInfos: RoleInfo[] = [
  {
    code: SysOrgRoleName.VIEW_SETUP,
    display: "View",
    comment: "View all setup pages.",
    level: 1,
  },
  {
    code: SysOrgRoleName.SETUP_BASIC,
    display: "Basic",
    comment: "View + Set up workflows and checklists.",
    level: 2,
  },
  {
    code: SysOrgRoleName.SETUP_ITEMS,
    display: "Items",
    comment: "Basic + Create and manage compliance items.",
    level: 3,
  },
];
const adminRoleInfos: RoleInfo[] = [
  {
    code: SysOrgRoleName.USER_ADMIN,
    display: "SysOrg and Users",
    comment: "Can manage users and roles in this organization.",
    level: 0, // not used
  },
];

/** Support ticket roles. Independent of each other and of the Setup ladder. */
const supportRoleInfos: RoleInfo[] = [
  {
    code: SysOrgRoleName.CLIENT_SUPPORT,
    display: "Client Support",
    comment: "See the internal view of support tickets in this organization.",
    level: 0, // not used
  },
  {
    code: SysOrgRoleName.HAVN_SUPPORT,
    display: "HAVN Support",
    comment:
      "Client Support + work tickets across all organizations and systems.",
    level: 0, // not used
  },
];
const selectedSetupRoleInfos = ref<SysOrgRoleName[]>([]);
const selectedAdminRoleInfos = ref<SysOrgRoleName[]>([]);
const selectedSupportRoleInfos = ref<SysOrgRoleName[]>([]);
const rolesToSave = computed(() => {
  if (props.whoToShow === SysOrgRoleName.GUEST) {
    return [SysOrgRoleName.GUEST];
  }
  return [
    isGuest.value ? SysOrgRoleName.GUEST : SysOrgRoleName.MEMBER,
    ...selectedSetupRoleInfos.value,
    ...selectedAdminRoleInfos.value,
    ...selectedSupportRoleInfos.value,
  ];
});

// const checkedSetupRoles = ref<RoleInfo[]>(
//   setupRoleInfos.filter((role) =>
//     user.value.currentOrgRoles?.includes(role.code),
//   ),
// );
// const checkedAdminRoles = ref<RoleInfo[]>(
//   adminRoleInfos.filter((role) =>
//     user.value.currentOrgRoles?.includes(role.code),
//   ),
// );

// roles to save
// const newRoles = ref<RoleInfo[]>([
//   ...setupRoleInfos.filter((r) => r.checked),
//   ...adminRoleInfos.filter((r) => r.checked),
// ]);
const emailIsUnique = computed(() => {
  return (
    props.existingEmails
      .filter((e) => e.userGuid !== user.value.userGuid)
      .filter((e) => e.email === user.value.email?.toLowerCase()).length === 0
  );
});

const usersWithSameEntityInstance = computed(() => {
  return props.userList
    ?.filter(
      (e) =>
        e.entity?.guid === selectedEntityInstanceGuid.value &&
        e.userGuid !== user.value.userGuid,
    )
    .map((e) => e.loginName);
});

// const showDefaultOrg = computed(
//   () =>
//     myInfoStore.userInfo?.userGuid === user.value.userGuid &&
//     myInfoStore.hasMultipleOrgs,
// );

const onSetupRoleChange = (role: RoleInfo) => {
  if (user.value.sysOrgRoles?.includes(role.code)) {
    // Remove roles at or above the selected role
    const updated = selectedSetupRoleInfos.value.filter((r) => {
      const ri = setupRoleInfos.find((ri) => ri.code === r);
      return !ri || ri.level < role.level;
    });

    selectedSetupRoleInfos.value = Array.from(new Set([...updated]));
  } else {
    // Reset to MEMBER and add all roles up to the selected one
    const updated = setupRoleInfos
      .filter((ri) => ri.level <= role.level)
      .map((ri) => ri.code as SysOrgRoleName);

    selectedSetupRoleInfos.value = Array.from(new Set([...updated]));
  }

  // remove all setup roles from user.value.currentOrgRoles
  const setupRoleCodes: string[] = setupRoleInfos.map((ri) => ri.code);
  user.value.sysOrgRoles = user.value.sysOrgRoles?.filter(
    (r) => !setupRoleCodes.includes(r) || r === SysOrgRoleName.MEMBER,
  );

  // add selected setup roles to user.value.currentOrgRoles
  user.value.sysOrgRoles = [
    ...(user.value.sysOrgRoles ?? []),
    ...selectedSetupRoleInfos.value,
  ];
};

const onAdminRoleChange = (selectedAdminRoleInfos: SysOrgRoleName[]) => {
  // remove all admin roles from user.value.currentOrgRoles
  const adminRoleCodes: string[] = adminRoleInfos.map((ri) => ri.code);
  user.value.sysOrgRoles = user.value.sysOrgRoles?.filter(
    (r) => !adminRoleCodes.includes(r),
  );
  selectedAdminRoleInfos.forEach((role) => {
    user.value.sysOrgRoles?.push(role);
  });
};

const onSupportRoleChange = (selected: SysOrgRoleName[]) => {
  // Replace whichever support roles were held with the current selection. Same shape as
  // onAdminRoleChange: these are independent toggles, not a ladder like the Setup roles.
  const supportRoleCodes: string[] = supportRoleInfos.map((ri) => ri.code);
  user.value.sysOrgRoles = user.value.sysOrgRoles?.filter(
    (r) => !supportRoleCodes.includes(r),
  );
  selected.forEach((role) => {
    user.value.sysOrgRoles?.push(role);
  });
};

const canSetGuest = computed(() => {
  const firstRoleCode =
    (rolesToSave.value.length && rolesToSave.value[0]) || "";
  return (
    rolesToSave.value.length === 1 &&
    (firstRoleCode === SysOrgRoleName.MEMBER ||
      firstRoleCode === SysOrgRoleName.GUEST)
  );
});

const addingGuest = computed(() => {
  return addingNewUser.value && props.whoToShow === SysOrgRoleName.GUEST;
});

const isGuest = computed({
  get(): boolean {
    return (
      (addingGuest.value ||
        user.value.sysOrgRoles?.includes(SysOrgRoleName.GUEST)) ??
      false
    );
  },
  set(becomeGuest: boolean) {
    if (becomeGuest) {
      // remove Member and add Guest
      // only do this if user has no other roles
      if (!canSetGuest.value) {
        return;
      }
      user.value.sysOrgRoles = [SysOrgRoleName.GUEST];
    } else {
      // remove Guest and add Member
      user.value.sysOrgRoles = [SysOrgRoleName.MEMBER];
    }
  },
});

const isMember = computed(() => !isGuest.value);

const entityCast = computed(() => user.value.entity as Entity);

const isValidEmail = computed(() => {
  const email = user.value.email;

  if (!email) {
    return false;
  }

  if (!isValidEmailAddress(email)) {
    return false;
  }

  return true;
});

/** edit history for the user */
const editHistory = useRefHistory(user, { deep: true });

type issueType = {
  d: string;
  o?: string | null;
  n?: string | null;
};

const changesToSave = computed(() => {
  const issues: issueType[] = [];
  const originalUser = editHistory.history.value.at(-1)?.snapshot;
  if (originalUser) {
    if (originalUser.loginName !== user.value.loginName) {
      issues.push({
        d: "Name changed",
        o: originalUser.loginName,
        n: user.value.loginName,
      });
    }
    if (originalUser.email !== user.value.email) {
      issues.push({ d: "Email changed", o: originalUser.email });
    }
    if (originalUser.defaultSysOrgGuid !== user.value.defaultSysOrgGuid) {
      issues.push({ d: "Starting SysOrg changed" });
    }
    if (originalUser.superAdmin !== user.value.superAdmin) {
      issues.push({ d: "SuperAdmin changed" });
    }
    if (originalUser.entity?.guid !== selectedEntityInstanceGuid.value) {
      issues.push({ d: "Entity changed" });
    }
    if (!arraysAreIdentical(originalUser.sysOrgRoles, user.value.sysOrgRoles)) {
      issues.push({ d: "Roles changed" });
    }
  }
  return issues;
});

const saveNeeded = computed(() => changesToSave.value.length > 0);

const canSaveIssues = computed(() => {
  const issues: issueType[] = [];
  if (!user.value.loginName) {
    issues.push({
      d: "Name needed",
    });
  }
  if (!user.value.email) {
    issues.push({ d: "Email needed" });
  } else if (!isValidEmail.value) {
    issues.push({ d: "Email not valid" });
  } else if (!emailIsUnique.value) {
    issues.push({ d: "Email already registered" });
  } else if (user.value.sysOrgRoles?.length === 0) {
    issues.push({ d: "At least one role needed" });
  }
  return issues;
});
const canSave = computed(() => canSaveIssues.value.length === 0);

const handleClose = async () => {
  if (saveNeeded.value) {
    ElMessageBox.confirm("You have unsaved changes.", "Unsaved Changes", {
      cancelButtonText: "Discard",
      confirmButtonText: "Continue Editing",
      closeOnPressEscape: false,
      closeOnClickModal: false,
      showClose: false,
      type: "warning",
    })
      // If the user confirms
      .then(() => {
        // do nothing
      })
      // If the user cancels
      .catch(() => {
        cancelChanges();
        emit("drawerClosed");
      });
  } else {
    emit("drawerClosed");
  }
};

const selectedEntityDefinitionGuid = ref<string>(
  user.value.entity?.entityDefinitionGuid ?? "",
);

const selectedEntityInstanceGuid = ref<string | undefined>(
  user.value.entity?.guid ?? undefined,
);

const cancelChanges = () => {
  const originalUser = editHistory.history.value.at(-1)?.snapshot;
  if (originalUser) {
    user.value.loginName = originalUser.loginName;
    user.value.email = originalUser.email;
    user.value.defaultSysOrgGuid = originalUser.defaultSysOrgGuid;
  }
  // currentGlobalLoginRoles.value = [...initialGlobalLoginRoles.value];
};

const setNewToOriginal = () => {
  editHistory.clear();
  // initialGlobalLoginRoles.value = [...currentGlobalLoginRoles.value];
};

const saveChanges = async () => {
  const entity = computed(() => {
    return entities.value.filter(
      (entity) => entity.guid === selectedEntityInstanceGuid.value,
    );
  });
  user.value.loginName = user.value.loginName?.trim(); // remove extra spaces

  const selectedEntity = entity.value[0];

  if (!selectedEntityInstanceGuid.value) {
    user.value.entity = undefined;
  }

  if (selectedEntity) {
    user.value.entity = selectedEntity;
  }

  // send to server
  if (addingNewUser.value) {
    user.value.sysOrgGuids = [];
    user.value.sysOrgGuids.push(myInfoStore.currentSysOrgGuid);
    user.value.defaultSysOrgGuid = myInfoStore.currentSysOrgGuid;

    await apiClient.loginAccount
      .createLoginAccount(user.value)
      .then((loginAccountInOrgDto) => {
        user.value = loginAccountInOrgDto as localLoginAccount;
        emit("updated", loginAccountInOrgDto);
        ElMessage.success("Saved");
        showSendEmail.value = loginAccountInOrgDto.isTempAccount;
      })
      .catch(showError);
  } else {
    await apiClient.loginAccountManagement
      .updateLoginAccountInSysOrg(myInfoStore.currentSysOrgGuid, user.value)
      .then((loginAccountInOrgDto: LoginAccountInSysOrgDto) => {
        user.value = loginAccountInOrgDto as localLoginAccount;
        emit("updated", loginAccountInOrgDto);
        ElMessage.success("Saved");
        showSendEmail.value = loginAccountInOrgDto.isTempAccount;
      })
      .catch(showError);
  }

  return;
};

const removeUserFromOrg = async () => {
  ElMessageBox.confirm(
    "Are you sure you want to delete this user?",
    "Delete User",
    {
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      closeOnPressEscape: false,
      closeOnClickModal: false,
      showClose: false,
      type: "warning",
    },
  )
    .then(async () => {
      await apiClient.loginAccountManagement
        .deleteLoginAccountInSysOrg(
          myInfoStore.currentSysOrgGuid,
          user.value.userGuid,
        )
        .then(() => {
          ElMessage.success("User deleted");
          emit("updated", user.value);
          emit("drawerClosed");
        })
        .catch(showError);
    })
    .catch(async () => {
      // do nothing
    });
};

const onSave = async () => {
  await saveChanges();
  setNewToOriginal(); // this is to disable the save button again
};

const selectedEntityChanged = ref(false);

watch(
  () => showSendEmail,
  () => {
    keySendEmail.value++;
  },
);

watch(
  () => selectedEntityInstanceGuid.value,
  () => {
    selectedEntityChanged.value = true;
  },
);

watch(
  () => props.drawerOpen,
  () => {
    if (!props.user.sysOrgRoles?.length) {
      if (addingGuest.value) {
        isGuest.value = true;
      } else {
        isGuest.value = false;
      }
    }
  },
  { immediate: true },
);

const retrieveOrgs = async () => {
  allOrgs.value = await apiClient.sysOrg.getOrgs();
};

const getEntities = async (entityDefinitionGuid: string) => {
  entities.value = await apiClient.entity.getEntities(
    myInfoStore.currentSysOrgGuid,
    entityDefinitionGuid,
  );
};

watch(
  () => entityInstanceCreatorOpen.value,
  async (newVal) => {
    if (!newVal && selectedEntityDefinitionGuid.value) {
      await getEntities(selectedEntityDefinitionGuid.value);
    }
  },
);
onMounted(async () => {
  await Promise.all([
    myInfoStore.load(),
    retrieveOrgs(),
    entityDefinitionStore.load(),
  ]);
  const userRoleCodes = user.value.sysOrgRoles ?? [];
  selectedSetupRoleInfos.value = setupRoleInfos
    .filter((r) => userRoleCodes?.includes(r.code))
    .map((r) => r.code);
  selectedAdminRoleInfos.value = adminRoleInfos
    .filter((r) => userRoleCodes?.includes(r.code))
    .map((r) => r.code);
  selectedSupportRoleInfos.value = supportRoleInfos
    .filter((r) => userRoleCodes?.includes(r.code))
    .map((r) => r.code);

  if (
    !userOrgs.value.find((org) => org.guid === user.value.defaultSysOrgGuid)
  ) {
    user.value.defaultSysOrgGuid = undefined;
  }

  if (addingNewUser.value) {
    selectedEntityDefinitionGuid.value =
      myInfoStore.currentSysOrg.entityDefinitionGuid ?? "";
  }

  if (selectedEntityDefinitionGuid.value) {
    getEntities(selectedEntityDefinitionGuid.value);
  }
});

const handleAddEntity = async (entity: Entity) => {
  selectedEntityInstanceGuid.value = entity.guid;
};

const toggle = computed(() => {
  return saveNeeded.value ? "Cancel" : "Close";
});
</script>
<template>
  <el-drawer
    v-if="user"
    :model-value="drawerOpen"
    :title="user.loginName ?? undefined"
    :before-close="handleClose"
    :destroy-on-close="true"
    direction="rtl"
    :size="drawerWidth"
    class="user-drawer"
  >
    <div class="user-info">
      <el-form :model="user" label-width="auto">
        <el-form-item label="Name">
          <el-input
            v-model="user.loginName"
            style="width: 325px"
            placeholder="Name"
            :disabled="!hasUserAdminRole"
          />
        </el-form-item>
        <el-form-item label="Email">
          <el-input
            v-if="isTempAccount"
            v-model.trim="user.email"
            style="width: 325px"
            placeholder="Email"
            show-word-limit
            maxlength="100"
            :disabled="!hasUserAdminRole"
            @change="() => isValidEmail"
          />
          <div v-else>{{ user.email }}</div>
          <span v-if="isTempAccount" class="tempAccount"
            >This person has not signed in yet.</span
          >

          <!-- <div :key="keySendEmail">
            <el-button
              v-if="showSendEmail"
              style="margin-left: 50px"
              type="primary"
              >Send invite email</el-button
            >
          </div> -->
        </el-form-item>
        <el-form-item label="Membership">
          <el-switch
            v-model="isGuest"
            class="guestSwitch"
            active-text="Guest"
            inactive-text="Member"
            :disabled="addingNewUser || !hasUserAdminRole || !canSetGuest"
          ></el-switch>
        </el-form-item>
        <el-form-item v-if="isMember" label="Setup">
          <div class="roles">
            <el-checkbox-group v-model="selectedSetupRoleInfos">
              <div v-for="role in setupRoleInfos" :key="role.code">
                <el-checkbox
                  :value="role.code"
                  :disabled="!hasUserAdminRole"
                  @change="onSetupRoleChange(role)"
                >
                  {{ role.display }}
                </el-checkbox>
                <span class="comment">{{ role.comment }}</span>
              </div>
            </el-checkbox-group>
          </div>
        </el-form-item>
        <el-form-item v-if="isMember" label="Administration">
          <div class="roles">
            <el-checkbox-group
              v-model="selectedAdminRoleInfos"
              @change="onAdminRoleChange(selectedAdminRoleInfos)"
            >
              <div v-for="role in adminRoleInfos" :key="role.code">
                <el-checkbox :disabled="!hasUserAdminRole" :value="role.code">
                  {{ role.display }}
                </el-checkbox>
                <span class="comment">{{ role.comment }}</span>
              </div>
            </el-checkbox-group>
          </div>
        </el-form-item>
        <el-form-item v-if="isMember" label="Support">
          <div class="roles">
            <el-checkbox-group
              v-model="selectedSupportRoleInfos"
              @change="onSupportRoleChange(selectedSupportRoleInfos)"
            >
              <div v-for="role in supportRoleInfos" :key="role.code">
                <el-checkbox :disabled="!hasUserAdminRole" :value="role.code">
                  {{ role.display }}
                </el-checkbox>
                <span class="comment">{{ role.comment }}</span>
              </div>
            </el-checkbox-group>
          </div>
        </el-form-item>

        <h4>Entity Association</h4>

        <el-form-item label="Entity Type of User Account">
          <el-select
            v-model="selectedEntityDefinitionGuid"
            :empty-values="[null, undefined]"
            style="width: 240px"
            @change="getEntities(selectedEntityDefinitionGuid)"
          >
            <el-option
              v-for="entity in entityDefinitionStore.EntitiesDefinitionList"
              :key="entity.guid"
              :label="entity.name ?? undefined"
              :value="entity.guid"
            />
          </el-select>
        </el-form-item>
        <el-form-item v-if="selectedEntityDefinitionGuid" label="Entity">
          <el-select
            v-model="selectedEntityInstanceGuid"
            :empty-values="[null, undefined]"
            style="width: 240px"
            clearable
          >
            <el-option
              v-for="entity in entities"
              :key="entity.guid"
              :label="entity.name ?? undefined"
              :value="entity.guid"
            />
          </el-select>
          <el-button
            v-if="selectedEntityDefinitionGuid"
            :icon="CirclePlus"
            class="addButton"
            @click="entityInstanceCreatorOpen = true"
          >
            New
            {{
              entityDefinitionStore.nameOfType(selectedEntityDefinitionGuid)
            }}</el-button
          >
          <span
            v-if="user.entity && !selectedEntityChanged"
            class="clickable-text"
            style="
              margin-left: 10px;
              cursor: pointer;
              text-decoration: underline;
            "
            @click="entityDrawerOpen = true"
          >
            View Details
          </span>
        </el-form-item>

        <el-form-item
          v-if="user.entity && usersWithSameEntityInstance?.length"
          style="margin-top: 1em"
        >
          <div>
            <span class="entity-comment">
              Others users with the same entity instance:
              {{ usersWithSameEntityInstance?.join(", ") }}
            </span>
          </div>
        </el-form-item>

        <el-form-item v-if="canSaveIssues.length" label="Issues">
          <ul class="unsavedChanges">
            <li v-for="(issue, index) in canSaveIssues" :key="issue.d ?? index">
              <el-text type="danger">
                {{ issue.d ?? issue }}
              </el-text>
            </li>
          </ul>
        </el-form-item>
        <el-form-item style="margin-top: 20px" label="&nbsp;">
          <el-button
            type="primary"
            :disabled="!saveNeeded || !canSave"
            @click="onSave"
            >Save</el-button
          >
          <el-button
            :type="toggle === 'Cancel' ? 'danger' : ''"
            :plain="toggle === 'Cancel'"
            @click="handleClose"
          >
            {{ toggle }}
          </el-button>
        </el-form-item>
        <el-form-item
          v-if="showDebugInfo && changesToSave.length"
          label="To Save"
        >
          <ul class="unsavedChanges">
            <li v-for="issue in changesToSave" :key="issue">
              {{ issue.d ?? issue }}
            </li>
          </ul>
        </el-form-item>

        <el-form-item
          v-if="user.userOrgNames && user.userOrgNames.length > 1"
          label="Organizations"
          style="margin-top: 1em"
        >
          <div>
            <li v-for="(orgName, index) in user.userOrgNames" :key="index">
              {{ orgName }}
            </li>
          </div>
        </el-form-item>
      </el-form>
      <el-form
        v-if="myInfoStore.isSuperAdmin"
        :model="user"
        label-width="auto"
        class="superAdmin"
        :class="{ notSelected: !user.superAdmin }"
      >
        <el-form-item label="Super Admin"
          ><el-checkbox
            v-model="user.superAdmin"
            :disabled="!myInfoStore.isSuperAdmin"
          >
          </el-checkbox>
        </el-form-item>
        <div>
          Caution: This applies to the entire website, not just this
          organization.
        </div>
      </el-form>
      <div v-if="!addingNewUser" class="userGuid">
        <div>
          <el-button type="danger" plain @click="removeUserFromOrg"
            >Delete User</el-button
          >
        </div>
        {{ user.userGuid }}
      </div>
    </div>
  </el-drawer>

  <EntityResponseViewer
    v-if="selectedEntityInstanceGuid && entityDrawerOpen"
    :key="selectedEntityInstanceGuid"
    :entity="entityCast"
    @close-drawer="entityDrawerOpen = false"
  />
  <EntityInstanceCreator
    v-if="selectedEntityDefinitionGuid && entityInstanceCreatorOpen"
    :key="selectedEntityDefinitionGuid"
    v-model="entityInstanceCreatorOpen"
    :entity-definition-guid="selectedEntityDefinitionGuid"
    :suggested-name="user.loginName ?? undefined"
    @add:entity="handleAddEntity"
  />
</template>
<style lang="less">
.user-drawer {
  display: flex;
  justify-content: space-between;
  padding-bottom: 1rem;

  .el-drawer__header {
    margin-bottom: 1rem;

    .el-drawer__title {
      padding-left: 0.8rem;
      font-size: 2rem;
      color: var(--el-color-black);
    }
  }

  .el-drawer__body {
    padding: 0 4rem 1rem 2rem;

    .user-info {
      display: grid;
      grid-template-columns: 1fr;
      gap: 2em;
      padding-top: 1.5rem;
      .el-table {
        margin-top: 1em;
      }
    }
  }
  .tempAccount {
    font-size: 0.9rem;
    margin-left: 1em;
    color: var(--el-color-primary-light-3);
  }
  .comment {
    font-size: 0.9rem;
    margin: 0 0 0 1em;
    color: var(--el-color-primary-light-5);
  }
  .entity-comment {
    font-size: 0.9rem;
    color: var(--el-color-primary-light-5);
    margin-left: 170px;
  }
  .userGuid {
    display: flex;
    justify-content: space-between;
    position: absolute;
    font-size: 0.8rem;
    color: var(--el-color-primary-light-8);
    right: 1em;
    bottom: 1em;
    width: 98%;
    align-items: center;
  }
  .roles {
    flex-direction: column;
    align-items: flex-start;
  }
  .guestSwitch {
    --el-switch-off-color: var(--el-color-success);
    --el-switch-on-color: var(--el-color-warning);
  }
  .unsavedChanges {
    margin: 0;
    font-size: inherit;
  }
  .superAdmin {
    height: fit-content;
    padding: 0.5em 1em 0.8em;
    font-size: 0.75em;
    border: solid 2px var(--el-color-danger-light-3);
    border-radius: 7px;
    background-color: var(--el-color-danger-light-8);
    right: 50px;
    width: 140px;
    position: absolute;

    .el-form-item__label {
      font-size: 1.15em;
      color: var(--el-color-danger);
    }

    &.notSelected {
      border: solid 2px var(--el-color-info-light-3);
      background-color: var(--el-color-info-light-8);
      opacity: 0.5;
      &:hover {
        opacity: 1;
        border-color: var(--el-color-danger-light-3);
      }

      .el-form-item__label {
        color: var(--el-color-info);
      }
    }
  }
}
</style>
